import * as React from 'react';

// Utils
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { IRootState } from '@src/store';
import { searchSelectors } from '@src/shared/src/selectors';
import { findBySelected, verifyProperty } from '@src/shared/src/util/general';
import { getMilageText } from '@src/shared/src/util/rentals';
import { getOperatorImageDetailURL } from '@toolkit/util/app';
import { formatDate, formatDayDifference } from '@src/shared/src/util/date';
import { getRentalTariffDocuments } from '@src/shared/src/util/rentals';

// Constants
import { DATE_FORMAT_TYPES, VEHICLE_TYPES } from '@src/shared/src/const/app';

// Actions
// Models
import { BasketItemModel, RentalFareModel, RentalModel } from '@src/shared/src/models';
// Interfaces
// Components
import ConfirmedBookingTravelInfoItem from './ConfirmedBookingTravelInfoItem';
import { BookingInfoItem } from '@src/components/BookingInfoItem';
import ConfirmedBookingDocuments from './ConfirmedBookingDocuments';
import ConfirmedBookingHeader from './ConfirmedBookingHeader';
import Badge from '@toolkit/ui/Badge';

// Styles
import '../styles/ConfirmedBooking.scss';

type Props = {
  rental: RentalModel;
  basketItems: BasketItemModel[];
  basketStatus: string;
};

const ConfirmedRental: React.FC<Props> = (props) => {
  const { rental, basketItems, basketStatus } = props;
  const { t } = useTranslation();

  const search = useSelector((state: IRootState) => searchSelectors.search(state.search));
  const selectedRentalFare: RentalFareModel = findBySelected(rental.fares);

  const dayDifference = formatDayDifference(search.depAt, search.arrAt);
  const rentalDays = `${dayDifference} ${dayDifference > 1 ? t('global.days') : t('global.day')}`;

  return (
    <div className="confirmed-booking">
      <div className="confirmed-booking-inner">
        <ConfirmedBookingHeader
          title={t('bookingDetails.rental.title')}
          image={getOperatorImageDetailURL(
            rental.rentalSearch.aggregator.effectiveIdent,
            VEHICLE_TYPES.RENTAL,
          )}
          date={formatDate(search.depAt, DATE_FORMAT_TYPES.DAY_LONG_DATE)}
          headerBadge={<Badge label={rentalDays} icon={'event'} />}
        />

        <BookingInfoItem
          icon={<i className="material-symbols">store</i>}
          depName={`${t('RentalPickUpInfo.label.pickUp')}	\u2219 ${
            rental.rentalSearch.depStationName
          }`}
          depAt={formatDate(search.depAt, DATE_FORMAT_TYPES.LONG_DATE_TIME)}
          arrName={`${t('RentalPickUpInfo.label.return')} \u2219 ${
            rental.rentalSearch.arrStationName
          }`}
          arrAt={formatDate(search.arrAt, DATE_FORMAT_TYPES.LONG_DATE_TIME)}
          changeover={true}
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">directions_car</i>}
          title={t('bookingDetails.rental.label.vehicle')}
          value={`${verifyProperty('', selectedRentalFare, ['vehicleExamples', '0'])} | ${
            rental.vehicle.group
          }`}
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">person</i>}
          title={t('RentalDescription.label.drivers')}
          value={search.passengers
            .map((passenger) => passenger.firstName + ' ' + passenger.lastName)
            .join(', ')}
        />

        {selectedRentalFare.extras.length > 0 && (
          <div className="confirmed-booking-extras">
            <h2 className="confirmed-booking-title">{t('RentalCarExtras.label.carExtras')}</h2>

            <ConfirmedBookingTravelInfoItem
              icon={<i className="material-symbols">note_stack_add</i>}
              title={t('RentalCarExtras.label.carExtras')}
              value={`${selectedRentalFare.extras.map(
                (extra) =>
                  `${extra.amountSelected > 1 ? `${extra.amountSelected}x ` : ''} ${extra.name}`,
              )}`}
            />
          </div>
        )}
        <ConfirmedBookingDocuments
          documents={getRentalTariffDocuments(basketItems, selectedRentalFare.id)}
          basketStatus={basketStatus}
        />
      </div>
      <div className="confirmed-booking-travel-info">
        <div className="confirmed-booking-travel-info-title">
          {t('BookingTravelInfo.title.rent')}
        </div>
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">route</i>}
          value={getMilageText(selectedRentalFare)}
        />
        <ConfirmedBookingTravelInfoItem
          icon={<i className="material-symbols">event_busy</i>}
          value={t('bookingDetails.title.Cancellation')}
          subtitle={t('bookingDetails.subtitle.rentalCancelation')}
        />
        {verifyProperty(-1, rental, ['vehicle', 'passengerCount']) > 0 && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">group</i>}
            value={`${rental.vehicle.passengerCount.toString()} ${
              rental.vehicle.passengerCount === 1
                ? t('bookingDetails.label.passenger')
                : t('bookingDetails.label.passengers')
            }`}
          />
        )}
        {verifyProperty(-1, rental, ['vehicle', 'doorsCount']) > 0 && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">car_rental</i>}
            value={`${rental.vehicle.doorsCount.toString()} ${t(
              'bookingDetails.label.doorsCount',
            )}`}
          />
        )}
        {verifyProperty(-1, rental, ['vehicle', 'airCondition']) > 0 &&
          rental.vehicle.airCondition && (
            <ConfirmedBookingTravelInfoItem
              icon={<i className="material-symbols">ac_unit</i>}
              value={t('bookingDetails.value.airCondition')}
            />
          )}
        {verifyProperty(-1, rental, ['vehicle', 'baggageBig']) > 0 && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">checked_bag</i>}
            value={`${rental.vehicle.baggageBig.toString()} ${t(
              'bookingDetails.label.baggageBig',
            )}`}
          />
        )}
        {verifyProperty(-1, rental, ['vehicle', 'baggageSmall']) > 0 && (
          <ConfirmedBookingTravelInfoItem
            icon={<i className="material-symbols">carry_on_bag</i>}
            value={`${rental.vehicle.baggageSmall.toString()} ${t(
              'bookingDetails.label.baggageSmall',
            )}`}
          />
        )}
      </div>
    </div>
  );
};

export default ConfirmedRental;
